import { useEffect, useState } from 'react';
import classNames from 'classnames';
import styles from './Modal.module.scss';
import IconCloseCross from 'ui/icons/icon_close_cross.svg';
import StripesIcon from 'ui/icons/icon-stripes.svg';
import BasketOutlinedIcon from 'ui/icons/icon-basket-outlined-white.svg';
// import FocusTrap from 'focus-trap-react';
import { Button, Heading, Icon, Paragraph } from 'ui/components';
import { ModalTab } from './ModalTab';
import { ModalTabContent } from './ModalTabContent';
import { ModalProductImages } from './ModalProductImages';
import { Grid } from 'ui/components/4-habitats';
import { CategoryFilterProps } from '../CategoryFilter/CategoryFilter';
import { ModalForm } from './ModalForm';
import IconCloseWebsite from 'ui/icons/icon_close_website.svg';

export interface ModalProps {
	className?: string;
	modalType: 'formModal' | 'languageSelectorModal' | 'productDetailsModal' | 'basketToastNotification' | 'M30ProductFeatures_Modal';
	title?: string;
	acceptText?: string;
	ariaClose?: string;
	children?: React.ReactNode;
	continents?: Umbraco.Continent[];
	categoryFilter?: CategoryFilterProps;
	handleClose: () => void;
	handleClick?: (...args: unknown[]) => void;
	product?: Models.CategoryProduct;
	activeSlide?: number | string;
	openToastNoti?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
	className,
	modalType,
	title,
	acceptText,
	ariaClose,
	children,
	continents,
	categoryFilter,
	handleClose,
	handleClick,
	product,
	activeSlide,
	openToastNoti,
}) => {
	const continentsData = modalType === 'languageSelectorModal' ? continents?.map((continent) => continent?.properties) : null;
	const [isActive, setIsActive] = useState<string>(continentsData && continentsData[0]?.continentName);

	const closeEscape = (event) => {
		if (event.keyCode === 27) {
			handleClose();
		}
	};

	useEffect(() => {
		document.addEventListener('keydown', closeEscape);

		return () => {
			document.removeEventListener('keydown', closeEscape);
		};
	});

	useEffect(() => {
		if (openToastNoti) {
			const timer = setTimeout(() => {
				handleClose(); // Automatically close the toast after 3.5 seconds
			}, 3500);
			return () => clearTimeout(timer);
		}
	}, [openToastNoti, handleClose]);

	const isWebsiteIcon = modalType === 'M30ProductFeatures_Modal' || modalType === 'languageSelectorModal';

	return (
		// <FocusTrap focusTrapOptions={trapOptions}>
		<div
			className={classNames(styles.Modal, className)}
			role={modalType === 'basketToastNotification' ? 'alert' : 'dialog'}
			aria-modal={true}
			aria-describedby="dialog-description"
		>
			<div
				className={classNames(styles.Modal_overlay, 'u-center-absolute', {
					[styles.Modal___M30ProductFeatures_ModalOverlay]: modalType === 'M30ProductFeatures_Modal' && !openToastNoti,
				})}
				onClick={handleClose}
			/>
			<div
				className={classNames(styles.Modal_modal, {
					[styles.Modal___formModal]: modalType === 'formModal',
					[styles.Modal___languageSelectorModal]: modalType === 'languageSelectorModal',
					[styles.Modal___productDetailsModal]: modalType === 'productDetailsModal',
					[styles.Modal___M30ProductFeatures_ModalInner]: modalType === 'M30ProductFeatures_Modal',
					// Toast notification for basket
					[styles.Modal___basketToastNotification]: modalType === 'basketToastNotification',
					[styles.Modal___slideDown]: modalType === 'basketToastNotification' && openToastNoti, // show notification
					[styles.Modal___slideUp]: modalType === 'basketToastNotification' && !openToastNoti, // hide notification
					[styles.Modal___M30ProductFeatures_Modal]: modalType === 'M30ProductFeatures_Modal',
				})}
			>
				{/* Close button */}
				<button
					className={classNames(styles.Modal_close, {
						[styles.Modal___formModal]: modalType === 'formModal',
						[styles.Modal___languageSelectorModal]: modalType === 'languageSelectorModal',
						[styles.Modal___productDetailsModal]: modalType === 'productDetailsModal',
						[styles.Modal___basketToastNotification]: modalType === 'basketToastNotification',
						[styles.Modal___Website_ModalCloseIcon]: isWebsiteIcon,
					})}
					onClick={handleClose}
					aria-label={ariaClose}
				>
					<Icon
						className={classNames(styles.Modal_closeIcon, {
							[styles.Modal___Website_ModalCloseIcon]: isWebsiteIcon,
						})}
					>
						{isWebsiteIcon ? <IconCloseWebsite /> : <IconCloseCross />}
					</Icon>
				</button>
				<Icon
					size="xl"
					className={classNames(styles.Modal_icon, {
						[styles.Modal___formModal]: modalType === 'formModal',
						[styles.Modal___languageSelectorModal]: modalType === 'languageSelectorModal',
						[styles.Modal___productDetailsModal]: modalType === 'productDetailsModal',
						[styles.Modal___M30ProductFeatures_ModalIcon]: modalType === 'M30ProductFeatures_Modal',
					})}
				>
					{modalType === 'basketToastNotification' && <BasketOutlinedIcon />}
				</Icon>
				{title && modalType === 'basketToastNotification' ? (
					<Paragraph style="sm" className={classNames(styles.Modal_toastNotificationText)}>
						{title}
					</Paragraph>
				) : (
					<Heading
						headingLevel="h3"
						style={modalType === 'formModal' ? 'xl' : 'lg'}
						className={classNames(styles.Modal_header, {
							[styles.Modal___formModal]: modalType === 'formModal',
							[styles.Modal___languageSelectorModal]: modalType === 'languageSelectorModal',
							[styles.Modal___M30ProductFeatures_ModalHeader]: modalType === 'M30ProductFeatures_Modal',
						})}
					>
						{title}
					</Heading>
				)}

				{children}

				{/* HELP FORM */}
				{modalType === 'formModal' && categoryFilter && <ModalForm categoryFilter={categoryFilter} />}

				{/* LANGUAGE SELECTOR */}
				{modalType === 'languageSelectorModal' && continentsData && (
					<div className={styles.Modal_tabMenu}>
						<ul className={styles.Modal_tabMenuContainer}>
							{continentsData?.map((item, index) => (
								<ModalTab
									key={index}
									continent={item.continentName}
									isActive={item.continentName === isActive ? true : false}
									setIsActive={setIsActive}
								/>
							))}
							<div className={styles.Modal_tabMenuBorder}></div>
						</ul>
						<Grid className={styles.Modal_tabMenuContentContainer}>
							{continentsData?.map((item, index) => (
								<ModalTabContent
									key={index}
									countries={item.countries}
									isActive={item.continentName === isActive ? true : false}
									handleClose={handleClose}
									handleClick={handleClick}
								/>
							))}
						</Grid>
					</div>
				)}

				{/* PRODUCT IMAGES */}
				{modalType === 'productDetailsModal' && product && <ModalProductImages product={product} activeSlide={activeSlide} />}

				{acceptText && (
					<Button style="primary" className={styles.Modal_accept} onClick={handleClose}>
						{' '}
						{acceptText}{' '}
					</Button>
				)}
			</div>
		</div>
		// </FocusTrap>
	);
};
